import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { Scan } from '../types';

interface ScanContextType {
  selectedScan: Scan | null;
  setSelectedScan: (scan: Scan | null) => void;
  clearSelectedScan: () => void;
}

const ScanContext = createContext<ScanContextType | undefined>(undefined);

export const ScanProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedScan, setSelectedScanState] = useState<Scan | null>(() => {
    // Try to restore from sessionStorage
    const stored = sessionStorage.getItem('selectedScan');
    return stored ? JSON.parse(stored) : null;
  });

  const setSelectedScan = useCallback((scan: Scan | null) => {
    setSelectedScanState(scan);
    if (scan) {
      sessionStorage.setItem('selectedScan', JSON.stringify(scan));
    } else {
      sessionStorage.removeItem('selectedScan');
    }
  }, []);

  const clearSelectedScan = useCallback(() => {
    setSelectedScanState(null);
    sessionStorage.removeItem('selectedScan');
  }, []);

  const value = useMemo(() => ({
    selectedScan, setSelectedScan, clearSelectedScan
  }), [selectedScan, setSelectedScan, clearSelectedScan]);

  return (
    <ScanContext.Provider value={value}>
      {children}
    </ScanContext.Provider>
  );
};

export const useScanContext = (): ScanContextType => {
  const context = useContext(ScanContext);
  if (context === undefined) {
    throw new Error('useScanContext must be used within a ScanProvider');
  }
  return context;
};

export default ScanContext;
